import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { setFeatureFlags } from '../services/featureFlags';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  });

  const logout = useCallback(() => {
    localStorage.removeItem('jwt');
    setFeatureFlags({});
    setAuthState({
      user: null,
      loading: false,
      isAuthenticated: false,
    });
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/users/me');
      if (response.ok) {
        const data = await response.json();
        setFeatureFlags(data.user.feature_flags);
        setAuthState({
          user: data.user,
          loading: false,
          isAuthenticated: true,
        });
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem('jwt');

    if (token) {
      fetchUser();
    } else {
      setAuthState({
        user: null,
        loading: false,
        isAuthenticated: false,
      });
    }
  }, [fetchUser]);

  const login = async (token: string) => {
    localStorage.setItem('jwt', token);
    setAuthState(prev => ({ ...prev, loading: true }));
    await fetchUser();
  };

  return {
    ...authState,
    login,
    logout,
    fetchUser,
  };
};
